import React, { useState, useEffect } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api/nocode';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import RadioGroup from '../../components/ui/RadioGroup';
import SafeIcon from '../../common/SafeIcon';

export default function Register() {
  const { user, setUser } = useAuth();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const plan = queryParams.get('plan'); // 'allaccess' or null

  const [activeTab, setActiveTab] = useState('new'); // 'new' | 'existing'
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Registration Setting State
  const [registrationOpen, setRegistrationOpen] = useState(true);
  const [checkingReg, setCheckingReg] = useState(true);

  // Form for New Users
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    genre: '',
    gender: '',
    journey_stage: '',
    wants_bunkmate: ''
  });

  // Form for Existing Users
  const [existingEmail, setExistingEmail] = useState('');

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await api.get('settings_fbwm');
        const data = Array.isArray(res) ? res : res.data || [];
        const regSetting = data.find(s => s.key === 'registration_open' || s.setting_key === 'registration_open');
        if (regSetting && regSetting.value === 'false') {
          setRegistrationOpen(false);
        }
      } catch (e) {
        console.error("Failed to check registration status", e);
      } finally {
        setCheckingReg(false);
      }
    };
    fetchSettings();
  }, []);

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  if (checkingReg) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center p-4">
        <div className="text-forest-green text-center">
          <SafeIcon name="Loader" className="animate-spin text-4xl mb-4 mx-auto" />
          <p className="font-bold">Checking registration status...</p>
        </div>
      </div>
    );
  }

  if (!registrationOpen) {
    return (
      <div className="min-h-screen bg-cream flex flex-col items-center justify-center p-4">
        <Card className="max-w-xl w-full text-center space-y-6 shadow-lg border-tan border-t-4 border-t-amber py-12">
          <div className="mx-auto w-16 h-16 bg-amber/20 rounded-full flex items-center justify-center mb-4 text-amber">
            <SafeIcon name="Lock" className="text-3xl" />
          </div>
          
          <h1 className="text-3xl font-bold text-dark-green mb-2">
            Registration is Closed
          </h1>
          
          <p className="text-lg text-muted mb-6 leading-relaxed">
            Camp FaiBoWriMo is not currently accepting new registrations. Join the waitlist to be notified when the next cohort opens!
          </p>

          <a href="https://join.crossandpen.com/p/camp-faibowrimo/" className="block w-full sm:w-auto mt-6">
            <Button className="py-4 px-10 text-lg bg-amber hover:bg-orange-600 text-dark-green font-bold shadow-md">
              Join the Waitlist
            </Button>
          </a>

          <div className="pt-8 mt-8 border-t border-tan text-sm">
            <span className="text-muted">Already have a Camp account? </span>
            <a href="https://gospelwiseauthor.app/login?redirect=https://camp.faibowrimo.com" className="text-forest-green font-bold hover:underline">
              Log in here
            </a>
          </div>
        </Card>
      </div>
    );
  }

  const handleNext = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) {
      setError('Please fill out all account fields.');
      return;
    }
    setError('');
    setStep(2);
  };

  const handleSubmitNewUser = async (e) => {
    e.preventDefault();
    if (!form.genre || !form.gender || !form.journey_stage || !form.wants_bunkmate) {
      setError('Please answer all questions to tailor your camp experience.');
      return;
    }

    setLoading(true);
    setError('');

   try {
  // 0. Check for an existing row with this email before inserting.
  const lookupRes = await api.get('users_fbwm');
  const existingRows = Array.isArray(lookupRes) ? lookupRes : lookupRes?.data || [];
  const existingUser = existingRows.find(
    (r) => (r.email || '').trim().toLowerCase() === form.email.trim().toLowerCase()
  );

  let userId;
  const bunkmateChoice = form.wants_bunkmate === '1' ? 1 : 0;

  if (existingUser && existingUser.first_login_complete === true) {
    setError('An account already exists for this email. Please log in instead.');
    setLoading(false);
    return;
  }

  if (existingUser) {
    // 1a. Claim the pre-existing tripwire-buyer row
    userId = existingUser.id;
    await api.put('users_fbwm', userId, {
      name: form.name,
      genre: form.genre,
      gender: form.gender,
      journey_stage: form.journey_stage,
      wants_bunkmate: bunkmateChoice,
      owner_id: existingUser.owner_id || userId,
    });
  } else {
    // 1b. No existing row — create camper profile directly in users_fbwm.
    const profileData = {
      name: form.name,
      email: form.email,
      genre: form.genre,
      gender: form.gender,
      journey_stage: form.journey_stage,
      wants_bunkmate: bunkmateChoice,
      role: 'camper',
      pass_type: plan === 'allaccess' ? 'allaccess' : 'free',
      first_login_complete: false,
      owner_id: 'pending',
    };

    const createRes = await api.post('users_fbwm', profileData);

    userId =
      createRes?.data?.id ||
      createRes?.id ||
      (Array.isArray(createRes?.data) ? createRes.data[0]?.id : null);

    // 2. Backfill owner_id now that we have the real NCB row ID
    if (userId) {
      await api.put('users_fbwm', userId, {
        owner_id: userId,
      });
    }
  }

      // 3. Send payload to Hub — Hub creates the account and fires the verification email
      try {
        await fetch('https://gospelwiseauthor.app/camp-fbwm-register.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            secret: import.meta.env.VITE_HUB_WEBHOOK_SECRET || 'gwa_camp_secure_sync_2026_xyz',
            email: form.email,
            name: form.name,
            password: form.password,
            app_slug: 'camp',
            genre: form.genre,
            gender: form.gender,
            journey_stage: form.journey_stage,
            source: 'camp_registration',
          }),
        });
      } catch (webhookErr) {
        console.warn("Hub webhook failed, but camp registration succeeded", webhookErr);
      }

      // 3.5 Fire Pabbly Webhook for onboarding automation
      try {
        await fetch('https://connect.pabbly.com/webhook-listener/webhook/IjU3NjEwNTZhMDYzZTA0M2M1MjZlNTUzNCI_3D_pc/IjU3NjcwNTZlMDYzZTA0MzY1MjY5NTUzMzUxM2Ei_pc', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: form.email,
            name: form.name,
            password: form.password,
            genre: form.genre,
            gender: form.gender,
            journey_stage: form.journey_stage,
            wants_bunkmate: bunkmateChoice,
            plan: plan === 'allaccess' ? 'allaccess' : 'free',
            source: 'camp_registration',
          }),
        });
      } catch (pabblyErr) {
        console.warn("Pabbly webhook failed", pabblyErr);
      }

      // 4. Handle redirect based on plan intent
      if (plan === 'allaccess') {
        window.location.href = `https://gospelwiseauthor.com/checkout/2qmklnq1ddjjyhb?email=${encodeURIComponent(form.email)}`;
        return;
      }

      // 5. Free registration — show email verification confirmation screen
      setStep(3);

    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleExistingSubmit = async (e) => {
    e.preventDefault();
    if (!existingEmail) {
      setError('Please enter your email address.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Send Webhook to Hub to grant access
      await fetch('https://gospelwiseauthor.app/camp-fbwm-register.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          secret: import.meta.env.VITE_HUB_WEBHOOK_SECRET || 'gwa_camp_secure_sync_2026_xyz',
          email: existingEmail,
          app_slug: 'camp',
          action: 'grant_access',
          source: 'existing_hub_user',
        }),
      });
    } catch (webhookErr) {
      console.warn("Hub webhook failed", webhookErr);
    }

    // If upgrading, direct to checkout instead of logging in
    if (plan === 'allaccess') {
      window.location.href = `https://gospelwiseauthor.com/checkout/2qmklnq1ddjjyhb?email=${encodeURIComponent(existingEmail)}`;
    } else {
      // Free existing users login through Hub
      window.location.href = 'https://www.gospelwiseauthor.app/#/login?redirect=https://camp.faibowrimo.com';
    }
  };

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center p-4">
      <Card className="max-w-xl w-full text-center space-y-6 shadow-lg border-tan border-t-4 border-t-forest-green">
        <div className="mx-auto w-16 h-16 bg-forest-green rounded-full flex items-center justify-center mb-4 text-white">
          <SafeIcon name="Compass" className="text-3xl" />
        </div>
        
        <h1 className="text-2xl font-bold text-dark-green">
          {plan === 'allaccess' ? 'Register for All-Access' : 'Register for Camp FaiBoWriMo'}
        </h1>

        <div className="flex border-b border-tan mb-6">
          <button
            className={`flex-1 py-3 text-sm font-bold transition-colors ${activeTab === 'new' ? 'border-b-2 border-forest-green text-forest-green' : 'text-muted hover:text-dark-green bg-gray-50/50'}`}
            onClick={() => { setActiveTab('new'); setError(''); }}
          >
            New User
          </button>
          <button
            className={`flex-1 py-3 text-sm font-bold transition-colors ${activeTab === 'existing' ? 'border-b-2 border-forest-green text-forest-green' : 'text-muted hover:text-dark-green bg-gray-50/50'}`}
            onClick={() => { setActiveTab('existing'); setError(''); }}
          >
            Existing GWA Hub User
          </button>
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 text-red-600 text-sm rounded-md text-center">
            {error}
          </div>
        )}

        {plan === 'allaccess' && (
          <div className="bg-amber/10 border border-amber/30 text-dark-green p-3 rounded-md text-sm mb-4">
            <strong>All-Access Pass:</strong> Create your profile first, then you will be securely redirected to checkout.
          </div>
        )}

        {activeTab === 'new' ? (
          <>
            {/* Step indicator text — hidden on step 3 */}
            {step < 3 && (
              <p className="text-muted mb-6">
                {step === 1 ? 'Create your account to get started.' : 'A few final details to customize your workbook.'}
              </p>
            )}

            {/* ── Step 1: Account details ── */}
            {step === 1 && (
              <form onSubmit={handleNext} className="space-y-4 text-left">
                <Input
                  label="Full Name"
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  placeholder="e.g. Jane Doe"
                />
                <Input
                  label="Email Address"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                  placeholder="you@email.com"
                />
                <Input
                  label="Password"
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  required
                  placeholder="••••••••"
                />
                <div className="pt-4">
                  <Button type="submit" className="w-full py-3 text-lg">
                    Continue to Next Step <SafeIcon name="ArrowRight" className="ml-2" />
                  </Button>
                </div>
              </form>
            )}

            {/* ── Step 2: Profile details ── */}
            {step === 2 && (
              <form onSubmit={handleSubmitNewUser} className="space-y-6 text-left">
                <RadioGroup
                  label="What do you write?"
                  name="genre"
                  value={form.genre}
                  onChange={(val) => setForm({ ...form, genre: val })}
                  options={[
                    { value: 'fiction', label: 'Fiction' },
                    { value: 'nonfiction', label: 'Nonfiction' }
                  ]}
                  className="bg-gray-50/50 p-4 rounded-md border border-tan"
                />
                <RadioGroup
                  label="What is your gender?"
                  name="gender"
                  value={form.gender}
                  onChange={(val) => setForm({ ...form, gender: val })}
                  options={[
                    { value: 'male', label: 'Male' },
                    { value: 'female', label: 'Female' }
                  ]}
                  className="bg-gray-50/50 p-4 rounded-md border border-tan"
                />
                <RadioGroup
                  label="Where are you in your journey?"
                  name="journey_stage"
                  value={form.journey_stage}
                  onChange={(val) => setForm({ ...form, journey_stage: val })}
                  options={[
                    { value: 'idea', label: 'Idea' },
                    { value: 'draft', label: 'Draft in Progress' },
                    { value: 'rough', label: 'Rough Complete' },
                    { value: 'ready', label: 'Ready to Publish' },
                    { value: 'published', label: 'Already Published' }
                  ]}
                  className="bg-gray-50/50 p-4 rounded-md border border-tan"
                />
                
                <div className="space-y-1">
                  <RadioGroup
                    label="Would you like to be paired with a Bunkmate? *"
                    name="wants_bunkmate"
                    value={form.wants_bunkmate}
                    onChange={(val) => setForm({ ...form, wants_bunkmate: val })}
                    options={[
                      { value: '1', label: 'Yes, pair me up!' },
                      { value: '0', label: 'No, I\'ll write solo' }
                    ]}
                    className="bg-gray-50/50 p-4 rounded-md border border-tan"
                  />
                  <p className="text-xs text-muted italic px-2">
                    * Bunkmate pairing is an exclusive feature for All-Access Pass holders.
                  </p>
                </div>

                <div className="pt-4 flex gap-3">
                  <Button type="button" variant="outline" className="w-1/3" onClick={() => setStep(1)} disabled={loading}>
                    Back
                  </Button>
                  <Button type="submit" className="w-2/3 py-3 text-lg" disabled={loading}>
                    {loading ? 'Processing...' : (plan === 'allaccess' ? 'Continue to Checkout' : 'Complete Registration')}
                  </Button>
                </div>
              </form>
            )}

            {/* ── Step 3: Email verification confirmation ── */}
            {step === 3 && (
              <div className="space-y-4 text-center py-4">
                <div className="mx-auto w-16 h-16 bg-forest-green/10 rounded-full flex items-center justify-center">
                  <SafeIcon name="Mail" className="text-3xl text-forest-green" />
                </div>
                <h2 className="text-xl font-bold text-dark-green">Check your email!</h2>
                <p className="text-muted text-sm leading-relaxed">
                  We've sent a verification link to <strong>{form.email}</strong>. Click it to activate your account, then log in to access Camp FaiBoWriMo.
                </p>
                <p className="text-muted text-xs leading-relaxed">
                  Don't see it? Check your spam folder, or try logging in — verification may arrive within a few minutes.
                </p>
                <div className="pt-2">
                  <a href="https://www.gospelwiseauthor.app/#/login?redirect=https://camp.faibowrimo.com">
                    <Button className="w-full py-3">
                      Log In to Camp <SafeIcon name="ArrowRight" className="ml-2" />
                    </Button>
                  </a>
                </div>
              </div>
            )}
          </>
        ) : (
          <form onSubmit={handleExistingSubmit} className="space-y-6 text-left">
            <div className="bg-amber/10 p-4 rounded-md border border-amber/30">
              <p className="text-sm text-dark-green leading-relaxed">
                If you already have a GospelWise Author Hub account, you do not need to register again. Simply enter your email address to sync your profile.
              </p>
            </div>
            
            <Input
              label="Hub Email Address"
              type="email"
              value={existingEmail}
              onChange={(e) => setExistingEmail(e.target.value)}
              required
              placeholder="you@email.com"
            />

            <div className="pt-4">
              <Button type="submit" className="w-full py-3 text-lg" disabled={loading}>
                {loading ? 'Authorizing...' : (plan === 'allaccess' ? 'Continue to Checkout' : 'Grant Access & Log In')}
              </Button>
            </div>
          </form>
        )}

        <div className="pt-6 mt-4 border-t border-tan text-sm text-center">
          <span className="text-muted">Already have a Camp account? </span>
          <a href="https://gospelwiseauthor.app/login?redirect=https://camp.faibowrimo.com" className="text-forest-green font-bold hover:underline">
            Log in here
          </a>
        </div>
      </Card>
    </div>
  );
}